import { useTranslation } from 'react-i18next';

export interface FtpPoint {
  date: string;
  ftp: number;
}

export function FtpChart({ data }: Readonly<{ data: FtpPoint[] }>) {
  const { t } = useTranslation();
  const values = data.map((d) => d.ftp);
  const minFtp = Math.min(...values) - 10;
  const maxFtp = Math.max(...values) + 10;
  const range = maxFtp - minFtp || 1;

  const w = 600;
  const h = 200;
  const padX = 50;
  const padY = 20;
  const chartW = w - padX * 2;
  const chartH = h - padY * 2;

  const points = data.map((d, i) => {
    const x = padX + (i / (data.length - 1)) * chartW;
    const y = padY + chartH - ((d.ftp - minFtp) / range) * chartH;
    return { x, y, ...d };
  });

  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');

  return (
    <div className="mt-4">
      <h3 className="text-sm font-medium text-(--color-text-secondary) mb-2">{t('profile.cycling.ftpHistory')}</h3>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-auto">
        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map((pct) => {
          const y = padY + chartH * (1 - pct);
          const val = Math.round(minFtp + range * pct);
          return (
            <g key={pct}>
              <line x1={padX} y1={y} x2={w - padX} y2={y} stroke="var(--color-border)" strokeWidth="0.5" />
              <text x={padX - 5} y={y + 4} textAnchor="end" fill="var(--color-text-secondary)" fontSize="10">{val}</text>
            </g>
          );
        })}
        {/* Line */}
        <path d={linePath} fill="none" stroke="var(--color-accent)" strokeWidth="2" />
        {/* Dots */}
        {points.map((p) => (
          <circle key={p.date} cx={p.x} cy={p.y} r="3" fill="var(--color-accent)" />
        ))}
        {/* X axis labels */}
        {points.filter((_, i) => i === 0 || i === points.length - 1).map((p) => (
          <text key={p.date} x={p.x} y={h - 2} textAnchor="middle" fill="var(--color-text-secondary)" fontSize="9">
            {p.date.slice(0, 10)}
          </text>
        ))}
      </svg>
    </div>
  );
}
